// Event bootstrap. Resolves the tenant (slug query, subdomain, or custom-domain host) to a
// public event config + branding when live mode (VITE_USE_API=true) is on; otherwise stays
// in mock mode with a demo theme so the app runs with no backend.
import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { LEGACY_MOTIF, brandingThemeFromPreset, keepTypeDef, resolveBranding, resolveSettings, themePreset, watermarkStamp } from "@wedding/shared";
import type { KeepBranding, KeepType, PublicKeepConfig, ThemeMotif } from "@wedding/shared";
import { USE_API, tenantRef } from "./config";
import { api } from "./api";
import { setDownloadWatermark } from "./download";

type ResolvedBranding = ReturnType<typeof resolveBranding>;
type ResolvedSettings = ReturnType<typeof resolveSettings>;

/** The structural (non-color) bits of a Keep's theme: which decoration motif to draw, the
 * hero-name style class, and whether it's a dark-canvas theme. Derived from the theme preset
 * the Keep started from; legacy Keeps (no preset) fall back to the watercolor florals. */
export interface ResolvedTheme {
  motif: ThemeMotif;
  scriptClass: string;
  dark: boolean;
}
function resolveTheme(b: ResolvedBranding): ResolvedTheme {
  const preset = themePreset(b.theme.preset);
  if (preset) return { motif: preset.motif, scriptClass: preset.scriptClass, dark: !!preset.dark };
  return { motif: (b.theme.motif as ThemeMotif) || LEGACY_MOTIF, scriptClass: "", dark: !!b.theme.dark };
}

/** What to render for the resolved tenant.
 *  - loading  : still resolving a live Keep (brief).
 *  - ready    : show the gallery (published Keep, mock dev, or a ?theme preview).
 *  - private  : the Keep exists but isn't publicly shared → themed "gallery is private" page.
 *  - notfound : no Keep at this address. */
export type KeepStatus = "loading" | "ready" | "private" | "notfound";

interface KeepState {
  /** null in mock mode or while resolving. */
  keepId: string | null;
  /** Gathering type — drives the hero kicker + default search suggestions. */
  type: KeepType;
  coupleNames: string | null;
  /** Fully-resolved branding (always defined; demo defaults in mock mode). */
  branding: ResolvedBranding;
  /** Fully-resolved settings (faceMatch / watermark / autoApprove). */
  settings: ResolvedSettings;
  /** Structural theme (motif / hero style / dark). */
  theme: ResolvedTheme;
  status: KeepStatus;
  /** true once we have a real keepId and live mode is on. */
  live: boolean;
}

// Preview mode: the dashboard settings preview loads the guest app with `?theme=` to show a
// (themed) look preview for an unshared draft. In that mode we skip the live resolve so the
// preview always renders the themed gallery, never the private/not-found page.
const IS_PREVIEW = typeof window !== "undefined" && new URLSearchParams(window.location.search).has("theme");

// Demo branding for mock dev mode — reproduces the original shipped wedding theme so
// `pnpm web:dev` looks the same as before the multi-tenant refactor.
const DEMO_BRANDING: KeepBranding = {
  displayName: "Taner & Abbie",
  eventDate: "June 20 · 2026",
  eventDateNumeric: "06 · 20 · 2026",
  theme: {
    accent: "#cf4a8e",
    accentDeep: "#a8316f",
    accentSoft: "#f6dce9",
    page: "#efe7df",
    paper: "#fbf7f2",
    scriptFont: "Pinyon Script",
    density: "lush",
    momentsLayout: "rows",
  },
  copy: {
    successNames: ["Abbie", "Taner", "the Hagemans", "Sophia", "the dance floor", "golden hour"],
    suggestedSearches: ["Show me the first dance", "Find the cake cutting", "Sunset shots"],
  },
};

// Mock-mode branding. Supports `?theme=<id>` (plus optional &name/&date/&type) so any preset can
// be previewed without a backend — used by the dashboard's settings preview to show a draft Keep's
// theme (drafts aren't published, so the live resolve 404s and we stay in this mock state). These
// params are ignored the moment a real, published Keep resolves.
function previewParams(): URLSearchParams {
  return typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
}
function mockBranding(): KeepBranding {
  const q = previewParams();
  const p = themePreset(q.get("theme"));
  if (!p) return DEMO_BRANDING;
  return {
    ...DEMO_BRANDING,
    displayName: q.get("name") || DEMO_BRANDING.displayName,
    eventDate: q.get("date") || DEMO_BRANDING.eventDate,
    theme: { ...brandingThemeFromPreset(p), density: "lush", momentsLayout: "rows" },
  };
}
function mockType(): KeepType {
  return keepTypeDef(previewParams().get("type")).id;
}
const MOCK = resolveBranding(mockBranding());
const MOCK_TYPE = mockType();

// Preview override: `?state=private|notfound|loading` forces that state (mock/preview only) so the
// dashboard (and dev) can preview these pages. Ignored for a real, live-resolving visitor.
const PREVIEW_STATE = ((): KeepStatus | null => {
  const s = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("state") : null;
  return s === "private" || s === "notfound" || s === "loading" ? s : null;
})();
// In live mode we start in "loading" (unless previewing) so a real visitor never flashes the demo.
const INITIAL_STATUS: KeepStatus = USE_API && !IS_PREVIEW ? "loading" : PREVIEW_STATE ?? "ready";

const KeepContext = createContext<KeepState>({
  keepId: null,
  type: MOCK_TYPE,
  coupleNames: null,
  branding: MOCK,
  settings: resolveSettings(null),
  theme: resolveTheme(MOCK),
  status: INITIAL_STATUS,
  live: false,
});

export function KeepProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<KeepState>({
    keepId: null,
    type: MOCK_TYPE,
    coupleNames: null,
    branding: MOCK,
    settings: resolveSettings(null),
    theme: resolveTheme(MOCK),
    status: INITIAL_STATUS,
    live: false,
  });

  useEffect(() => {
    if (!USE_API || IS_PREVIEW) return;
    let cancelled = false;
    const ref = tenantRef();
    const resolve: Promise<PublicKeepConfig> =
      ref.kind === "slug" ? api.resolveKeep(ref.slug) : api.resolveKeepByHost(ref.host);
    resolve
      .then((info) => {
        if (cancelled) return;
        const branding = resolveBranding(info.branding);
        const settings = resolveSettings(info.settings);
        // Per-tenant tab title (the static index.html title is the generic fallback).
        if (branding.displayName) document.title = `${branding.displayName} · Photos`;
        // Watermark downloads with the event name + date when enabled.
        setDownloadWatermark(settings.watermark ? watermarkStamp(info.branding) : null);
        // Unpublished Keep → themed "gallery is private" page (still apply its theme).
        setState({
          keepId: info.keepId, type: info.type, coupleNames: info.coupleNames, branding, settings,
          theme: resolveTheme(branding), status: info.published ? "ready" : "private", live: true,
        });
      })
      .catch(() => {
        if (!cancelled) setState((s) => ({ ...s, status: "notfound" }));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Theme the document root (not just the .app card) so the desktop backdrop behind the phone
  // matches, and flag dark themes on <body>. Runs for both mock and live so ?theme= previews too.
  useEffect(() => {
    const t = state.branding.theme;
    const root = document.documentElement.style;
    root.setProperty("--c-page", t.page);
    root.setProperty("--c-paper", t.paper);
    root.setProperty("--c-accent", t.accent);
    root.setProperty("--c-accent-soft", t.accentSoft);
    document.body.classList.toggle("theme-dark", state.theme.dark);
  }, [state.branding, state.theme]);

  return <KeepContext.Provider value={state}>{children}</KeepContext.Provider>;
}

export function useKeep(): KeepState {
  return useContext(KeepContext);
}

/** Convenience: the Keep's structural theme (motif / hero style / dark). */
export function useKeepTheme(): ResolvedTheme {
  return useContext(KeepContext).theme;
}

/** Convenience: what to render for this tenant (ready / private / notfound / loading). */
export function useKeepStatus(): KeepStatus {
  return useContext(KeepContext).status;
}

/** Convenience: resolved branding (theme + copy), always fully populated. */
export function useBranding(): ResolvedBranding {
  return useContext(KeepContext).branding;
}

/** Convenience: resolved per-event settings (faceMatch / watermark / autoApprove). */
export function useSettings(): ResolvedSettings {
  return useContext(KeepContext).settings;
}

/** Convenience: the Keep's gathering type. */
export function useKeepType(): KeepType {
  return useContext(KeepContext).type;
}
